<template>
  <div class="text-center">
    <v-btn
      v-if="isGenerating"
      variant="flat"
      size="large"
      prepend-icon="mds:stop-circle"
      class="btn-stop-generating"
      @click="userStopGenerating()"
    >
      Stop generating
    </v-btn>
    <disclaimer-dialog
      ref="disclaimerDialog"
      @conditions-accepted="onConditionsAccepted()"
    />
    <v-dialog v-model="dialog" width="800">
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            <v-icon class="ml-2">mds:temp-preferences-custom</v-icon>&nbsp;&nbsp;
            <span>Generate workflow with AI</span>
          </v-toolbar-title>
          <help-dialog-workflow-ai
            #default="{ props }"
            content-class="dialog-top-right"
          >
            <v-btn variant="text" icon="mdi-help-circle-outline" v-bind="props" class="mr-2"></v-btn>
          </help-dialog-workflow-ai>
          <v-btn variant="text" icon color="grey" @click="dialog = false">
            <v-icon >mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text style="padding: 16px 24px 8px 24px">
          <v-form ref="formGenerateDiagram" v-model="validForm">
            <v-container class="flex-wrap pa-0">
              <v-col cols="12" class="pa-0">
                <ai-description-input
                  id="ai-description-input"
                  :text="workflow.aiDescription"
                  :attachments="workflow.aiAttachments || []"
                  :project-id="$route.params.projectId"
                  :workflow-id="$route.params.id"
                  @change-description="debouncedSaveAiDescription($event)"
                  @change-attachments="saveAiAttachments($event)"
                  label="Describe the business process or the scope of the project"
                  :items="examples"
                  ref="aiDescription"
                  :persistent-hint="true"
                  hint="Which roles are involved in the process? What are the main steps?"
                />
              </v-col>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-text class="my-0 py-0 text-black">
          <v-card class="pa-2 mt-0">
            <div
              class="card-section-header d-flex align-center px-2 py-1"
              @click="cardSectionExpanded = !cardSectionExpanded"
            >
              <span class="text-caption font-weight-medium text-uppercase flex-grow-1">
                For each step generate cards with AI
                <span v-if="selectedCardTypeCount > 0" class="text-medium-emphasis font-weight-regular text-none ml-1">
                  ({{ selectedCardTypeCount }} selected)
                </span>
              </span>
              <span @click.stop>
                <help-dialog-workflow-cards />
              </span>
              <v-icon
                :class="{ 'card-section-chevron--collapsed': !cardSectionExpanded }"
                class="card-section-chevron ml-1"
              >
                mdi-chevron-up
              </v-icon>
            </div>

            <v-expand-transition>
              <div v-show="cardSectionExpanded">
                <template v-for="cardType in cardTypes" :key="cardType.id">
                  <v-container class="flex-wrap align-center py-2">
                    <v-row class="mb-2">
                      <v-col cols="2" class="d-flex justify-center align-center">
                        <v-checkbox
                          :model-value="cardType.generateWithAI"
                          @update:model-value="saveGenerateCardWithAI(cardType)"
                          hide-details
                          :disabled="$store.state.isWorkflowDisabled"
                        ></v-checkbox>
                      </v-col>
                      <v-col cols="3" class="d-flex justify-left align-center">
                        <v-chip size="large" variant="elevated" :color="cardType.color.rgb">
                          <strong>{{cardType.title}}</strong>
                        </v-chip>
                      </v-col>
                      <v-col cols="7" class="d-flex type-title align-center">
                        {{ cardType.description }}
                      </v-col>
                    </v-row>
                    <v-row
                      v-if="cardType.domainModelRole === DomainModelRole.COMMAND && cardType.generateWithAI"
                      class="mb-2"
                    >
                      <v-col cols="5"></v-col>
                      <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                        <v-checkbox
                          v-model="generateCommand"
                          label="Generate Command Attributes with AI"
                          hide-details
                          :disabled="$store.state.isWorkflowDisabled"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                    <v-row
                      v-if="cardType.domainModelRole === DomainModelRole.READ_MODEL && cardType.generateWithAI"
                      class="mb-2"
                    >
                      <v-col cols="5"></v-col>
                      <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                        <v-checkbox
                          v-model="generateReadModel"
                          label="Generate Read Model Attributes with AI"
                          hide-details
                          :disabled="$store.state.isWorkflowDisabled"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                    <v-row
                      v-if="cardType.domainModelRole === DomainModelRole.AGGREGATE_ROOT && cardType.generateWithAI"
                      class="mb-2"
                    >
                      <v-col cols="5"></v-col>
                      <v-col cols="7" class="d-flex align-center py-0 ml-n3">
                        <v-checkbox
                          v-model="generateEntity"
                          label="Generate Entities with AI"
                          hide-details
                          :disabled="$store.state.isWorkflowDisabled"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-container>
                </template>
              </div>
            </v-expand-transition>

          </v-card>
        </v-card-text>
        <v-card-actions>
          <ai-version-selector class="flex-grow-1" :modelValue="selectedAiModel" />
          <v-btn variant="outlined" size="large" @click="dialog = false">Cancel</v-btn>
          <v-btn
            variant="flat"
            size="large"
            id="test-generate-btn"
            color="primary"
            :disabled="noModelsAvailable || !selectedAiModel"
            @click="generateDiagramFromText"
          >
            Generate workflow
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showValidationDialog" max-width="600px">
      <v-card>
        <div class="d-flex justify-space-between pa-2 pb-0">
          <v-card-title>
            <span class="text-h5">Validation issue</span>
          </v-card-title>
          <v-btn variant="text" icon color="grey" @click="cancelFromValidation">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text>{{ validationMessage }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="forceInvalidatedAction">Force generation</v-btn>
          <v-btn color="primary" variant="text" @click="editPromptFromValidation">Edit prompt</v-btn>
          <v-btn v-if="suggestedAiDescription" color="primary" @click="setSuggestion">Use suggestion</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Checks from '@/helpers/checks'
import cardTypesApi from '@/api/cardTypesApi'
import workflowApi from '@/api/workflowApi'
import randomUUID from '@/utils/uuid'
import store from '@/store'
import HelpDialogWorkflowAi from '@/components/HelpDialogWorkflowAi'
import HelpDialogWorkflowCards from '@/components/HelpDialogWorkflowCards'
import AiDescriptionInput from '@/components/AiDescriptionInput.vue'
import { EventBus } from '@/event-bus/event-bus.js'
import { mapGetters, mapState } from 'vuex'
import DisclaimerDialog from '@/components/DisclaimerDialog.vue'
import AiVersionSelector from '@/components/AiVersionSelector.vue'
import { effectiveAiVersions } from '@/shared/utils/effectiveAiVersions'
import { AI_MODELS } from '@/constants/aiModels'
import debounce from '@/utils/debounce'
import { scrollToEvent } from '@/utils/scroll'
import { formatCardKey } from '@/ai/utils/helpers'
import { toCamelCase } from '@/shared/codegen/utils'
import { sanitizeCardDescription } from '@/shared/utils/sanitize'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { workflowHelpers } from '@/mixins/workflowHelpers'
import { DomainModelRole } from '@/constants/cardTypes'
import { SchemaType } from '@/constants/schema'
import { findEntityIdByName } from '@/utils/fieldNameMatch'
import { workflowDescriptionExamples } from '@/ai/prompts/common/examples'
import { mergeReadModels } from '@/shared/utils/mergeReadModels'
import {
  parseEntityResponse,
  parseCommandFields,
  parseReadModelFields,
  parseDomainEventFields,
  assignBoundedContexts
} from '@/shared/utils/workflowSpec'
import {
  generateCommentReply,
  generateEntitiesFromWorkflow,
  generateSingleCard,
  generateSingleEvent,
  generateWorkflowCards,
  generateWorkflowDiagram,
  generateWorkflowReadModels,
  generateWorkflowCommands,
  generateWorkflowDomainEvents,
  validateWorkflowDescription
} from '@/ai'

export default {
  props: [
    'bpm',
    'events'
  ],
  components: {
    AiDescriptionInput,
    HelpDialogWorkflowAi,
    HelpDialogWorkflowCards,
    DisclaimerDialog,
    AiVersionSelector
  },
  methods: {
    openSettingsOrGenerator () {
      if (this.workflow.domainEvents && this.workflow.domainEvents.length > 0) {
        this.$store.dispatch('workflow/openWorkflowSettings')
        return
      }

      this.$emit('open-generator')
    },
    openSettings () {
      this.$refs.disclaimerDialog.openDialog()
    },
    onConditionsAccepted () {
      this.dialog = true
    },
    changeSelectedEvent: diagramHelpers.methods.changeSelectedEvent,
    async addReplyToDiagram (input, skipValidation = false) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        await store.dispatch('workflow/openWorkflowDescriptionDialog')
        return
      }
      let validationPassed = skipValidation

      try {
        if (!skipValidation) {
          const ok = await this.validatePrompt()
          if (!ok) {
            this.forcedAction = this.prepareForcedAction(this.addReplyToDiagram, input, true)
            this.$loadingState.stop()
            return
          }
          validationPassed = true
        }

        this.abortController = new AbortController()

        const res = await generateCommentReply({
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          input,
          abortController: this.abortController
        })

        if (res?.message) {
          await store.dispatch('workflow/addCardReply', {
            workflowId: input.workflowId,
            cardId: input.cardId,
            input: {
              text: res.message,
              generatedByAi: true
            }
          })
        } else {
          store.commit('snackbar/showMessage', {
            content: 'Error adding',
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          this.$loadingState.stop()
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$store.dispatch('workflow/openRetryDialog', {
            failedItems: [{ type: 'comment', name: 'Add Comment' }],
            retryCallback: () => this.addReplyToDiagram(input, validationPassed)
          })
        }
      } finally {
        this.$loadingState.stop()
      }
    },
    async saveGenerateCardWithAI (cardType) {
      const payload = {
        generateWithAI: !cardType.generateWithAI
      }
      await cardTypesApi.updateCardType(this.$route.params.id, cardType, payload)
    },
    userStopGenerating () {
      if (!this.abortController) {
        return
      }

      this.isGenerating = false
      this.$loadingState.stop()
      try {
        this.abortController.abort()
        this.abortController = null
      } catch (e) {
        // Ignore
      }
      store.commit('snackbar/showMessage', { content: 'Generation was stopped by the user', timeout: 3000, color: 'blue' }, { root: true })
    },
    async generateDiagramFromText (validateForm = true) {
      const currentDescription = this.$refs.aiDescription?.textModel ?? this.workflow.aiDescription

      if (currentDescription !== this.workflow.aiDescription) {
        this.saveAiDescriptionDebouncer.cancel()
        await this.saveAiDescription(currentDescription)
      }

      if (validateForm && !currentDescription && (this.workflow.aiAttachments || []).length === 0) {
        this.$store.commit('snackbar/showMessage', {
          content: 'Cannot generate workflow with empty prompt',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return
      }

      this.$nextTick(async () => {
        this.$loadingState.start('Validating the query...')

        let validationOk
        try {
          validationOk = await this.validatePrompt()
        } catch (e) {
          if (e.name !== 'AbortError') {
            this.$loadingState.stop()
            this.$store.dispatch('workflow/openRetryDialog', {
              failedItems: [{ type: 'validation', name: 'Prompt Validation' }],
              retryCallback: () => this.generateDiagramFromText(validateForm)
            })
          } else {
            this.$loadingState.stop()
          }
          return
        }

        if (!validationOk) {
          this.forcedAction = this.prepareForcedAction(this.generateDiagramFromText, validateForm)
          this.$loadingState.stop()
          return
        }

        this.dialog = false
        this.isGenerating = true
        this.$store.dispatch('workflow/clearFailedGenerations')
        this.readModelsForLinking = []
        this.domainEventsForLinking = []

        let diagramFailed = false
        let cardsFailed = false

        try {
          await this.createDiagram()
        } catch (e) {
          if (e.name !== 'AbortError') {
            diagramFailed = true
            this.$store.dispatch('workflow/addFailedGeneration', { type: 'diagram', name: 'Workflow Events & Roles', error: e?.message || String(e), errorName: e?.name })
          }
        }

        if (!diagramFailed) {
          try {
            await this.addCardsToEvents()
          } catch (e) {
            if (e.name !== 'AbortError') {
              cardsFailed = true
              this.$store.dispatch('workflow/addFailedGeneration', { type: 'cards', name: 'Cards', error: e?.message || String(e), errorName: e?.name })
            }
          }
        } else {
          cardsFailed = true
          this.$store.dispatch('workflow/addFailedGeneration', { type: 'cards', name: 'Cards' })
        }

        if (!diagramFailed && !cardsFailed) {
          await this.addSchemasToEvents()
        } else {
          const failedItems = [
            { type: 'commands', name: 'Commands with Data Fields' },
            { type: 'queries', name: 'Read Models with Data Field' },
            { type: 'entities', name: 'Entities & Relationships' }
          ]

          failedItems.forEach((item) => {
            this.$store.dispatch('workflow/addFailedGeneration', item)
          })
        }

        this.isGenerating = false
        this.$loadingState.stop()

        const hasFailures = this.failedGenerations.length > 0
        if (hasFailures) {
          this.$store.dispatch('workflow/openRetryDialog', {
            failedItems: this.failedGenerations,
            retryCallback: () => this.retryFailedGenerations()
          })
        } else {
          await this.createBoundedContexts()
          this.changeSelectedEvent(null)
          this.readModelsForLinking = []
          this.domainEventsForLinking = []
        }
      })
    },
    async retryFailedGenerations () {
      this.isGenerating = true
      this.$loadingState.start('Retrying failed generation steps...')

      const itemsToRetry = [...this.failedGenerations]
      this.$store.dispatch('workflow/clearFailedGenerations')

      let diagramFailed = false
      let cardsFailed = false
      let commandsFailed = false
      let queriesFailed = false
      let domainEventsFailed = false

      for (const item of itemsToRetry) {
        if (item.type === 'cards' && diagramFailed) {
          this.$store.dispatch('workflow/addFailedGeneration', item)
          cardsFailed = true
          continue
        }
        if (['commands', 'queries', 'domainEvents', 'entities'].includes(item.type) && (diagramFailed || cardsFailed)) {
          this.$store.dispatch('workflow/addFailedGeneration', item)
          if (item.type === 'commands') {
            commandsFailed = true
          }
          if (item.type === 'queries') {
            queriesFailed = true
          }
          if (item.type === 'domainEvents') {
            domainEventsFailed = true
          }
          continue
        }
        if (item.type === 'entities' && (commandsFailed || queriesFailed || domainEventsFailed)) {
          this.$store.dispatch('workflow/addFailedGeneration', item)
          continue
        }

        try {
          switch (item.type) {
            case 'diagram':
              await this.createDiagram()
              break
            case 'cards':
              await this.addCardsToEvents()
              break
            case 'commands':
              await this.generateCommands()
              break
            case 'queries':
              this.readModelsForLinking = await this.generateReadModels() || []
              break
            case 'domainEvents':
              this.domainEventsForLinking = await this.generateDomainEventSchemas() || []
              break
            case 'entities':
              await this.generateAndLinkEntities(this.readModelsForLinking, this.domainEventsForLinking)
              break
          }
        } catch (e) {
          if (e.name !== 'AbortError') {
            this.$store.dispatch('workflow/addFailedGeneration', item)
            if (item.type === 'diagram') {
              diagramFailed = true
              cardsFailed = true
              commandsFailed = true
              queriesFailed = true
              domainEventsFailed = true
            } else if (item.type === 'cards') {
              cardsFailed = true
              commandsFailed = true
              queriesFailed = true
              domainEventsFailed = true
            } else if (item.type === 'commands') {
              commandsFailed = true
            } else if (item.type === 'queries') {
              queriesFailed = true
            } else if (item.type === 'domainEvents') {
              domainEventsFailed = true
            }
          }
        }
      }

      this.isGenerating = false
      this.$loadingState.stop()

      const hasFailures = this.failedGenerations.length > 0
      if (hasFailures) {
        this.$store.dispatch('workflow/openRetryDialog', {
          failedItems: this.failedGenerations,
          retryCallback: () => this.retryFailedGenerations()
        })
      } else {
        await this.createBoundedContexts()
        store.commit('snackbar/showMessage', {
          content: 'All steps completed successfully!',
          timeout: 3000,
          color: 'green'
        }, { root: true })
        this.changeSelectedEvent(null)
        this.readModelsForLinking = []
        this.domainEventsForLinking = []
        this.$store.dispatch('workflow/clearFailedGenerations')
      }
    },
    debouncedSaveAiDescription ({ description, skipDebounce }) {
      if (skipDebounce) {
        this.saveAiDescriptionDebouncer.cancel()
        this.saveAiDescription(description)
      } else {
        this.saveAiDescriptionDebouncer(description)
      }
    },
    async saveAiDescription (description) {
      await workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiDescription: description
        }
      )
    },
    async saveAiAttachments (attachments) {
      const aiAttachments = (attachments || []).map((attachment) => ({
        s3Key: attachment.s3Key,
        name: attachment.name,
        mimeType: attachment.mimeType,
        size: attachment.size
      }))

      await workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiAttachments
        }
      )
    },
    addShapeToDiagram (parentId) {
      this.$loadingState.start()
      this.$nextTick(async () => {
        try {
          await this.addShape(parentId, this.bpm, this.cardTypes)
        } catch (e) {
          console.error(e)
        } finally {
          this.$loadingState.stop()
        }
      })
    },
    addCardToDiagram (eventId, cardTypeId) {
      this.$loadingState.start()
      this.$nextTick(async () => {
        try {
          await this.addCard(eventId, cardTypeId)
        } catch (e) {
          console.error(e)
        } finally {
          this.$loadingState.stop()
        }
      })
    },
    async cancellableRequest (promptFunction, ...args) {
      this.isGenerating = true
      const result = await promptFunction(...args)
      if (this.isGenerating) {
        this.isGenerating = false
        return result
      } else {
        return null
      }
    },
    async addShape (parentId, bpm, cardTypes, skipValidation = false) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        await store.dispatch('workflow/openWorkflowDescriptionDialog')
        return
      }
      this.$loadingState.start()

      let validationPassed = skipValidation

      try {
        if (!skipValidation) {
          const ok = await this.validatePrompt()
          if (!ok) {
            this.forcedAction = this.prepareForcedAction(this.addShape, parentId, bpm, cardTypes, true)
            this.$loadingState.stop()
            return
          }
          validationPassed = true
        }

        const controller = new AbortController()
        this.abortController = controller

        const response = await this.cancellableRequest(generateSingleEvent, {
          parentEventId: parentId,
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          abortController: controller
        })

        const step = response?.event

        if (step) {
          try {
            await this.runStep('create-shape', {
              type: '1',
              taskDescription: step.domainEventName,
              laneDescription: step.role,
              conditionLabel: step.conditionLabel,
              parentId
            })
            for (let i = 0; i < cardTypes.length; i++) {
              if (cardTypes[i].generateWithAI) {
                const cardType = formatCardKey(cardTypes[i].title)
                if (step[cardType]) {
                  await this.runStep('add-card', { cardTypeName: cardTypes[i].title, cardText: step[cardType] })
                }
              }
            }
            await this.addCommandSchemas(step)
            await this.linkEntities(
              step.readModelFields
                ? [{ readModelFields: step.readModelFields, queriedEntity: step.queriedEntity }]
                : []
            )
            await this.linkCommandAndQueryFieldReferences()
            store.commit('snackbar/showMessage', { content: 'Completed', timeout: 3000, color: 'blue' }, { root: true })
          } catch (error) {
            console.error(error)
            store.commit('snackbar/showMessage', { content: 'Error adding', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
          }
        } else {
          store.commit('snackbar/showMessage', {
            content: 'Error adding',
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          this.$loadingState.stop()
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$store.dispatch('workflow/openRetryDialog', {
            failedItems: [{ type: 'event', name: 'Add Event' }],
            retryCallback: () => this.addShape(parentId, bpm, cardTypes, validationPassed)
          })
        }
      } finally {
        this.$loadingState.stop()
      }
    },
    async addCard (eventId, cardTypeId, skipValidation = false) {
      if (!this.workflow.aiDescription) {
        this.$loadingState.stop()
        await store.dispatch('workflow/openWorkflowDescriptionDialog')
        return
      }

      let validationPassed = skipValidation

      try {
        if (!skipValidation) {
          const ok = await this.validatePrompt()
          if (!ok) {
            this.forcedAction = this.prepareForcedAction(this.addCard, eventId, cardTypeId, true)
            this.$loadingState.stop()
            return
          }
          validationPassed = true
        }

        const controller = new AbortController()
        this.abortController = controller

        const obj = await this.cancellableRequest(generateSingleCard, {
          eventId,
          workflow: this.workflow,
          cardTypeId,
          cardTypes: this.cardTypes,
          abortController: controller
        })

        if (obj) {
          try {
            const index = this.cardTypes.findIndex((cardType) => cardType.id === cardTypeId)
            if (formatCardKey(this.cardTypes[index].title) === Object.keys(obj)[0]) {
              await this.runStep('add-card', { cardTypeName: this.cardTypes[index].title, cardText: Object.values(obj)[0], eventId })
            }
            store.commit('snackbar/showMessage', { content: 'Completed', timeout: 3000, color: 'blue' }, { root: true })
          } catch (error) {
            console.error(error)
            store.commit('snackbar/showMessage', { content: 'Error adding', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
          }
        } else {
          store.commit('snackbar/showMessage', {
            content: 'Error adding',
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          this.$loadingState.stop()
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.$store.dispatch('workflow/openRetryDialog', {
            failedItems: [{ type: 'card', name: 'Add Card' }],
            retryCallback: () => this.addCard(eventId, cardTypeId, validationPassed)
          })
        }
      } finally {
        this.$loadingState.stop()
      }
    },
    async createDiagram () {
      if (!this.isGenerating) {
        return
      }
      this.$loadingState.setMessage('Creating events...')
      const controller = new AbortController()
      this.abortController = controller

      const diagramResponse = await generateWorkflowDiagram({
        workflow: this.workflow,
        cardTypes: this.cardTypes,
        attachments: this.workflow.aiAttachments || [],
        onChunkReceived: this.addEvents,
        abortController: controller
      })
      if (this.isGenerating) {
        if (diagramResponse?.length) {
          store.commit('snackbar/showMessage', {
            content: 'Added all events',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        } else {
          throw new Error('Empty response from events generation')
        }
      }
      return diagramResponse
    },
    async addCardsToEvents () {
      if (!this.isGenerating) {
        return
      }
      if (!this.cardTypes.filter((r) => r.generateWithAI).length) {
        return
      }
      this.$loadingState.setMessage('Adding cards to events...')
      const controller = new AbortController()
      this.abortController = controller

      const cardsResponse = await generateWorkflowCards({
        workflow: this.workflow,
        cardTypes: this.cardTypes,
        onChunkReceived: this.addCards,
        abortController: controller
      })
      if (this.isGenerating) {
        if (cardsResponse?.length) {
          store.commit('snackbar/showMessage', {
            content: 'Added all corresponding cards',
            timeout: 3000,
            color: 'blue'
          }, { root: true })
        } else {
          throw new Error('Empty response from cards generation')
        }
      }

      return cardsResponse
    },
    async addSchemasToEvents () {
      if (!this.isGenerating) {
        return
      }

      let commandsFailed = false
      let queriesFailed = false

      try {
        await this.generateCommands()
      } catch (e) {
        if (e.name !== 'AbortError') {
          commandsFailed = true
          this.$store.dispatch('workflow/addFailedGeneration', { type: 'commands', name: 'Commands with Data Fields', error: e?.message || String(e), errorName: e?.name })
        }
      }

      if (!this.isGenerating) {
        return
      }

      try {
        this.readModelsForLinking = await this.generateReadModels() || []
      } catch (e) {
        if (e.name !== 'AbortError') {
          queriesFailed = true
          this.$store.dispatch('workflow/addFailedGeneration', { type: 'queries', name: 'Read Models with Data Field', error: e?.message || String(e), errorName: e?.name })
        }
      }

      if (!this.isGenerating) {
        return
      }

      let domainEventsFailed = false

      try {
        this.domainEventsForLinking = await this.generateDomainEventSchemas() || []
      } catch (e) {
        if (e.name !== 'AbortError') {
          domainEventsFailed = true
          this.$store.dispatch('workflow/addFailedGeneration', { type: 'domainEvents', name: 'Domain Event Schemas', error: e?.message || String(e), errorName: e?.name })
        }
      }

      if (!this.isGenerating) {
        return
      }

      if (commandsFailed || queriesFailed || domainEventsFailed) {
        this.$store.dispatch('workflow/addFailedGeneration', { type: 'entities', name: 'Entities & Relationships' })
        return
      }

      try {
        await this.generateAndLinkEntities(this.readModelsForLinking, this.domainEventsForLinking)
      } catch (e) {
        if (e.name !== 'AbortError') {
          this.$store.dispatch('workflow/addFailedGeneration', { type: 'entities', name: 'Entities & Relationships', error: e?.message || String(e), errorName: e?.name })
        }
      }
    },
    async generateCommands () {
      const commandCardType = diagramHelpers.methods.getCommandCardType(this.cardTypes)
      if (this.generateCommand && commandCardType?.generateWithAI) {
        this.$loadingState.setMessage('Adding commands to events...')
        const controller = new AbortController()
        this.abortController = controller

        const commandsResponse = await generateWorkflowCommands({
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          onChunkReceived: this.addCommandSchemas,
          abortController: controller
        })
        if (this.isGenerating) {
          if (commandsResponse?.length) {
            store.commit('snackbar/showMessage', {
              content: 'Added all corresponding commands',
              timeout: 3000,
              color: 'blue'
            }, { root: true })
          } else {
            throw new Error('Empty response from commands generation')
          }
        }
        return commandsResponse
      }
      return null
    },
    async generateReadModels () {
      const readModelCardType = diagramHelpers.methods.getQueryCardType(this.cardTypes)
      if (this.generateReadModel && readModelCardType?.generateWithAI) {
        this.$loadingState.setMessage('Adding read models to events...')
        const controller = new AbortController()
        this.abortController = controller

        const readModelResponse = await generateWorkflowReadModels({
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          onChunkReceived: () => {},
          abortController: controller
        })

        if (this.isGenerating) {
          if (readModelResponse?.length) {
            const mergedForLinking = await this.persistMergedReadModels(readModelResponse)
            store.commit('snackbar/showMessage', {
              content: 'Added all corresponding read models',
              timeout: 3000,
              color: 'blue'
            }, { root: true })

            return mergedForLinking
          } else {
            throw new Error('Empty response from read models generation')
          }
        }

        return readModelResponse
      }

      return null
    },
    async persistMergedReadModels (readModelResponse) {
      const mergedGroups = mergeReadModels(readModelResponse)
      const readModelToGroup = new Map()
      const groupToSchemaId = new Map()
      for (const group of mergedGroups) {
        groupToSchemaId.set(group, randomUUID())
        for (const readModel of group.members) {
          readModelToGroup.set(readModel, group)
        }
      }

      const createdSchemas = new Set()
      const mergedForLinking = []

      for (const readModel of readModelResponse) {
        const group = readModelToGroup.get(readModel)
        if (!group) {
          continue
        }
        const event = this.getEventFromEventObj(readModel)
        if (!event) {
          continue
        }
        const card = cardHelpers.methods.getQueryCards(event)[0]
        if (!card) {
          continue
        }

        const schemaId = groupToSchemaId.get(group)

        if (createdSchemas.has(schemaId)) {
          await this.runStep('link-read-model-card', {
            schemaId,
            cardId: card.id,
            eventId: event.id
          })
          continue
        }

        await this.runStep('add-merged-read-model', {
          schemaId,
          schemaName: sanitizeCardDescription(card.description) || SchemaType.QUERY,
          description: group.description,
          fields: parseReadModelFields(group.mergedFields),
          cardId: card.id,
          eventId: event.id,
          cardinality: group.cardinality
        })
        createdSchemas.add(schemaId)

        mergedForLinking.push({
          queriedEntity: group.queriedEntity,
          cardinality: group.cardinality,
          readModelFields: group.mergedFields
        })
      }

      return mergedForLinking
    },
    async generateDomainEventSchemas () {
      const domainEventCardType = diagramHelpers.methods.getDomainEventCardType(this.cardTypes)
      if (domainEventCardType?.generateWithAI) {
        this.$loadingState.setMessage('Adding domain event schemas...')
        const controller = new AbortController()
        this.abortController = controller

        const domainEventsResponse = await generateWorkflowDomainEvents({
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          onChunkReceived: this.addDomainEventSchemas,
          abortController: controller
        })
        if (this.isGenerating) {
          if (domainEventsResponse?.length) {
            store.commit('snackbar/showMessage', {
              content: 'Added all domain event schemas',
              timeout: 3000,
              color: 'blue'
            }, { root: true })
          } else {
            throw new Error('Empty response from domain events generation')
          }
        }

        return domainEventsResponse
      }

      return null
    },
    async generateAndLinkEntities (readModelsForLinking = [], domainEventsForLinking = []) {
      const entityCardType = diagramHelpers.methods.getAggregateCardType(this.cardTypes)
      if (this.generateEntity && entityCardType?.generateWithAI) {
        this.$loadingState.setMessage('Adding entities to workflow...')
        const controller = new AbortController()
        this.abortController = controller

        const entitiesResponse = await generateEntitiesFromWorkflow({
          workflow: this.workflow,
          cardTypes: this.cardTypes,
          stream: true,
          onChunkReceived: this.addEntity,
          abortController: controller
        })
        if (this.isGenerating) {
          if (entitiesResponse?.length) {
            store.commit('snackbar/showMessage', {
              content: 'Added all corresponding entities',
              timeout: 3000,
              color: 'blue'
            }, { root: true })
          } else {
            throw new Error('Empty response from entities generation')
          }
        }

        // Retain the raw AI entities (which carry their bounded context names) so
        // createBoundedContexts can group entities even when it runs after a retry.
        this.generatedEntitiesResponse = entitiesResponse || []

        await this.resolveEntityCrossReferences(entitiesResponse)

        await this.$nextTick()
        this.$loadingState.setMessage('Linking entities to events...')
        await this.linkEntities(readModelsForLinking, domainEventsForLinking)
        await this.linkCommandAndQueryFieldReferences()
      }
    },
    async createBoundedContexts () {
      const schemas = this.workflow.schemas || []
      const entitySchemas = schemas.filter((s) => s.type === SchemaType.ENTITY)
      const hasExistingBoundedContexts = (this.workflow.boundedContexts || []).length > 0

      if (entitySchemas.length === 0 || hasExistingBoundedContexts) {
        return
      }

      // Resolve the bounded contexts the AI assigned to the generated entities.
      const { boundedContexts, entityBcId } = assignBoundedContexts(
        entitySchemas,
        this.generatedEntitiesResponse,
        this.workflow.name,
        randomUUID
      )

      await store.dispatch('workflow/updateWorkflow', {
        projectId: this.$route.params.projectId,
        input: {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          boundedContexts,
          updateNote: {
            action: boundedContexts.length > 1 ? 'added bounded contexts' : 'added bounded context',
            target: boundedContexts.map((bc) => bc.name).join(', ')
          }
        }
      })

      const updatedSchemas = (this.workflow.schemas ?? []).map((schema) => ({
        ...schema,
        boundedContext: schema.type === SchemaType.ENTITY
          ? (entityBcId.get(schema.id) ?? schema.boundedContext)
          : schema.boundedContext
      }))

      await store.dispatch('workflow/updateWorkflow', {
        projectId: this.$route.params.projectId,
        input: {
          id: this.workflow.id,
          expectedVersion: this.workflow.version,
          schemas: updatedSchemas,
          updateNote: {
            action: 'updated bounded context on entities',
            target: ''
          }
        }
      })
    },
    getEventFromEventObj (obj) {
      const eventName = obj.domainEventName || obj.name
      const lane = obj.role
        ? Object.values(this.bpm.svgLanes).find((lane) => lane.name === obj.role)
        : null

      if (lane) {
        return this.workflow.domainEvents?.find((event) => event.description === eventName && event.laneId === lane.id)
      }

      return this.workflow.domainEvents?.find((event) => event.description === eventName)
    },
    updateRouteWithEventId (eventId) {
      this.$router.push({ path: '/workflow/' + this.$route.params.projectId + '/' + this.workflow.id + '/' + eventId }).catch(() => { })
      scrollToEvent(eventId)
    },
    async addEvents (eventObject) {
      await this.runStep('create-shape', {
        type: '1',
        taskDescription: eventObject.domainEvent,
        laneDescription: eventObject.role
      })
    },
    async addCards (obj) {
      const event = this.getEventFromEventObj(obj)
      if (!event) {
        return
      }
      for (let i = 0; i < this.cardTypes?.length; i++) {
        if (this.cardTypes[i].generateWithAI) {
          const cardTypeKey = formatCardKey(this.cardTypes[i].title)
          const isTagCard = this.cardTypes[i].domainModelRole === DomainModelRole.TAG

          if (isTagCard) {
            const tags = Array.isArray(obj[cardTypeKey]) ? obj[cardTypeKey] : []
            if (tags.length > 0) {
              this.updateRouteWithEventId(event.id)
              await this.runStep('add-card', { cardTypeName: this.cardTypes[i].title, cardText: tags, eventId: event.id })
            }
          } else {
            if (!Array.isArray(obj[cardTypeKey])) {
              obj[cardTypeKey] = [obj[cardTypeKey]]
            }
            for (const cardData of obj[cardTypeKey]) {
              this.updateRouteWithEventId(event.id)
              const cardText = typeof cardData === 'object' ? cardData.name : cardData
              await this.runStep('add-card', { cardTypeName: this.cardTypes[i].title, cardText, eventId: event.id })
            }
          }
        }
      }
    },
    async addCommandSchemas (obj) {
      const event = this.getEventFromEventObj(obj)
      if (!event) {
        return
      }
      const commandCard = cardHelpers.methods.getCommandCard(event)
      const queryCard = cardHelpers.methods.getQueryCards(event)

      if (obj.fields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-command-schema', {
          example_data: obj.fields,
          required: obj.required,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: obj.type,
          eventId: event.id,
          cardId: obj.cardId || commandCard?.id || null
        })
      }

      if (obj.readModelFields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-command-schema', {
          example_data: obj.readModelFields,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: SchemaType.QUERY,
          eventId: event.id,
          cardId: obj.cardId || queryCard[0]?.id || null
        })
      }
    },
    async addDomainEventSchemas (obj) {
      const event = this.getEventFromEventObj(obj)
      if (!event) {
        return
      }
      const domainEventCard = cardHelpers.methods.getDomainEventCard(event)

      if (obj.domainEventFields?.length > 0) {
        this.updateRouteWithEventId(event.id)
        await this.runStep('add-domain-event-schema', {
          example_data: obj.domainEventFields,
          taskDescription: obj.description,
          laneDescription: obj.role,
          type: SchemaType.DOMAIN_EVENT,
          eventId: event.id,
          cardId: obj.cardId || domainEventCard?.id || null
        })
      }
    },
    async resolveEntityCrossReferences (entitiesFromLLM) {
      if (!entitiesFromLLM?.length) {
        return
      }

      const schemas = this.workflow.schemas || []
      const entityNameToIdMap = new Map()
      for (const schema of schemas) {
        if (schema.type === SchemaType.ENTITY) {
          entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
        }
      }

      let needsUpdate = false
      const updatedSchemas = safeStructuredClone(schemas)

      for (const entityFromLLM of entitiesFromLLM) {
        const parsed = parseEntityResponse(entityFromLLM, entityNameToIdMap)
        const schemaIndex = updatedSchemas.findIndex((s) => s.type === SchemaType.ENTITY && s.name === entityFromLLM.name)
        if (schemaIndex === -1) {
          continue
        }

        const existingFields = updatedSchemas[schemaIndex].fields || []
        const fieldByName = new Map(existingFields.map((field) => [field.name.toLowerCase(), field]))

        for (const parsedField of parsed.fields) {
          if (!parsedField.relatedEntityId) {
            continue
          }
          const existingField = fieldByName.get(parsedField.name.toLowerCase())
          if (existingField && !existingField.relatedEntityId) {
            existingField.relatedEntityId = parsedField.relatedEntityId
            needsUpdate = true
          }
        }
      }

      if (needsUpdate) {
        await workflowApi.updateSchemas({
          schemas: updatedSchemas,
          workflow: this.workflow
        })
      }
    },
    async addEntity (entityFromLLM) {
      const schemas = this.workflow.schemas || []
      const existingEntityNames = new Set(
        schemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase())
      )

      if (existingEntityNames.has(entityFromLLM.name.toLowerCase())) {
        return
      }

      const entityNameToIdMap = new Map()
      for (const schema of schemas) {
        if (schema.type === SchemaType.ENTITY) {
          entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
        }
      }

      const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
      generatedEntity.id = randomUUID()

      const newSchemas = [...schemas, generatedEntity]
      await workflowApi.updateSchemas({
        schemas: newSchemas,
        workflow: this.workflow
      })
    },
    async linkEntities (readModels, domainEventsForLinking = []) {
      const entities = this.workflow.schemas || []
      const chunkSize = 5
      const events = this.workflow.domainEvents
      const updatedSchemas = safeStructuredClone(this.workflow.schemas || [])
      let schemasNeedUpdate = false

      for (let i = 0; i < events.length; i += chunkSize) {
        const chunk = events.slice(i, i + chunkSize)
        const updatedSvgGraph = safeStructuredClone(this.bpm.svgGraph)
        if (!updatedSvgGraph) {
          continue
        }

        chunk.forEach((event) => {
          if (!updatedSvgGraph[event.id]) {
            return
          }
          updatedSvgGraph[event.id].cards = updatedSvgGraph[event.id].cards?.map((item) => {
            // For AggregateRoot cards: set schemaId to the entity
            if (item.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT) {
              const entityId = entities.find((entity) => entity.name === item.description)?.id
              return {
                ...item,
                schemaId: item.schemaId || entityId
              }
            }

            // For ReadModel cards: update the Query schema's entityId
            if (item.cardType?.domainModelRole === DomainModelRole.READ_MODEL && item.schemaId) {
              const eventReadModel = this.workflow.schemas?.find((s) => s.id === item.schemaId)
              if (eventReadModel) {
                const fields = (eventReadModel.fields || []).map((f) => f.name).join(',')
                const matchingReadModel = readModels?.find((rm) => {
                  const fieldNames = (rm.readModelFields || []).map((f) => f.name).join(',')
                  return fieldNames === fields
                })

                if (matchingReadModel) {
                  const entityId = entities.find((entity) => entity.name === matchingReadModel.queriedEntity)?.id
                  if (entityId) {
                    // Update schema entityId in the schemas array
                    const schemaIndex = updatedSchemas.findIndex((s) => s.id === item.schemaId)
                    if (schemaIndex !== -1) {
                      updatedSchemas[schemaIndex].entityId = entityId
                      schemasNeedUpdate = true
                    }
                  }
                }
              }
            }

            // For DomainEvent cards: update the DomainEvent schema's entityId
            if (item.cardType?.domainModelRole === DomainModelRole.DOMAIN_EVENT && item.schemaId) {
              const domainEventSchema = this.workflow.schemas?.find((s) => s.id === item.schemaId)
              if (domainEventSchema) {
                const fields = (domainEventSchema.fields || []).map((field) => field.name).join(',')
                const matchingDomainEvent = domainEventsForLinking.find((de) => {
                  const fieldNames = (de.domainEventFields || []).map((field) => field.name).join(',')
                  return fieldNames === fields
                })

                if (matchingDomainEvent?.domainEventEntity) {
                  const entityId = entities.find((entity) => entity.name === matchingDomainEvent.domainEventEntity)?.id
                  if (entityId) {
                    const schemaIndex = updatedSchemas.findIndex((s) => s.id === item.schemaId)
                    if (schemaIndex !== -1 && !updatedSchemas[schemaIndex].entityId) {
                      updatedSchemas[schemaIndex].entityId = entityId
                      schemasNeedUpdate = true
                    }
                  }
                }
              }
            }

            return item
          })
        })

        await workflowApi.updateWorkflow({
          svgGraph: updatedSvgGraph,
          schemas: schemasNeedUpdate ? updatedSchemas : undefined,
          workflow: this.workflow,
          updateNote: {
            action: 'updated root entity',
            target: ''
          }
        }, true)

        await this.$nextTick()
      }
    },
    buildFieldReferenceMaps () {
      const schemas = this.workflow.schemas || []
      const entities = schemas.filter((s) => s.type === SchemaType.ENTITY)
      const events = this.workflow.domainEvents || []

      const entityByIdMap = new Map()
      for (const entity of entities) {
        entityByIdMap.set(entity.id, entity)
      }

      const entityNameToIdMap = new Map()
      for (const entity of entities) {
        entityNameToIdMap.set(toCamelCase(entity.name), entity.id)
      }

      const commandSchemaToEntityMap = new Map()
      for (const event of events) {
        const cards = this.bpm.svgGraph[event.id]?.cards || []
        const commandCard = cards.find((c) => c.cardType?.domainModelRole === DomainModelRole.COMMAND)
        const entityCard = cards.find((c) => c.cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT)
        if (commandCard?.schemaId && entityCard?.schemaId) {
          commandSchemaToEntityMap.set(commandCard.schemaId, entityCard.schemaId)
        }
      }

      return { entityByIdMap, entityNameToIdMap, commandSchemaToEntityMap }
    },
    processSchemaFields (fields, fieldRefMap, entityNameToIdMap, entityByIdMap) {
      let changed = false
      for (const field of fields || []) {
        const camelCaseFieldName = toCamelCase(field.name)
        const matchingEntityId = findEntityIdByName(camelCaseFieldName, entityNameToIdMap)
        if (matchingEntityId && field.fields && field.fields.length > 0 && !field.relatedEntityId) {
          field.relatedEntityId = matchingEntityId
          changed = true
        }

        const relatedEntityId = fieldRefMap.get(field.name.toLowerCase())
        if (relatedEntityId && !field.relatedEntityId) {
          field.relatedEntityId = relatedEntityId
          changed = true
        }

        if (field.fields && field.fields.length > 0) {
          let childFieldRefMap = fieldRefMap
          if (field.relatedEntityId) {
            const relatedEntity = entityByIdMap.get(field.relatedEntityId)
            if (relatedEntity) {
              childFieldRefMap = workflowHelpers.methods.getEntityFieldRefMap(relatedEntity)
            }
          }
          if (this.processSchemaFields(field.fields, childFieldRefMap, entityNameToIdMap, entityByIdMap)) {
            changed = true
          }
        }
      }

      return changed
    },
    async linkCommandFieldReferences ({ entityByIdMap, entityNameToIdMap, commandSchemaToEntityMap }) {
      const targets = []
      for (const schema of this.workflow.schemas || []) {
        if (schema.type !== SchemaType.COMMAND) {
          continue
        }
        const entityId = commandSchemaToEntityMap.get(schema.id)
        if (!entityId) {
          continue
        }
        const targetEntity = entityByIdMap.get(entityId)
        if (!targetEntity) {
          continue
        }
        targets.push({ schemaId: schema.id, targetEntity })
      }

      const chunkSize = 5
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize)
        const schemas = safeStructuredClone(this.workflow.schemas || [])
        let updated = false

        for (const { schemaId, targetEntity } of chunk) {
          const schema = schemas.find((s) => s.id === schemaId)
          if (!schema) {
            continue
          }
          const fieldRefMap = workflowHelpers.methods.getEntityFieldRefMap(targetEntity)
          if (this.processSchemaFields(schema.fields, fieldRefMap, entityNameToIdMap, entityByIdMap)) {
            updated = true
          }
        }

        if (updated) {
          await workflowApi.updateSchemas({ schemas, workflow: this.workflow })
        }
      }
    },
    async linkQueryFieldReferences ({ entityByIdMap, entityNameToIdMap }) {
      const targets = []
      for (const schema of this.workflow.schemas || []) {
        if (schema.type !== SchemaType.QUERY || !schema.entityId) {
          continue
        }
        const targetEntity = entityByIdMap.get(schema.entityId)
        if (!targetEntity) {
          continue
        }
        targets.push({ schemaId: schema.id, targetEntity })
      }

      const chunkSize = 5
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize)
        const schemas = safeStructuredClone(this.workflow.schemas || [])
        let updated = false

        for (const { schemaId, targetEntity } of chunk) {
          const schema = schemas.find((s) => s.id === schemaId)
          if (!schema) {
            continue
          }
          const fieldRefMap = workflowHelpers.methods.getEntityFieldRefMap(targetEntity)
          if (this.processSchemaFields(schema.fields, fieldRefMap, entityNameToIdMap, entityByIdMap)) {
            updated = true
          }
        }

        if (updated) {
          await workflowApi.updateSchemas({ schemas, workflow: this.workflow })
        }
      }
    },
    async linkCommandAndQueryFieldReferences () {
      const entities = (this.workflow.schemas || []).filter((s) => s.type === SchemaType.ENTITY)
      if (entities.length === 0) {
        return
      }

      const maps = this.buildFieldReferenceMaps()
      await this.linkCommandFieldReferences(maps)
      await this.linkQueryFieldReferences(maps)
    },
    async runStep (operation, args = null) {
      const delay = 300
      let oldVersion = this.workflow.version
      switch (operation) {
        case 'create-shape':
          await this.bpm.addShape({
            description: args.taskDescription,
            conditionLabels: args.conditionLabel && args.parentId
              ? [{ parentId: args.parentId, name: args.conditionLabel }]
              : undefined
          }, {
            laneName: args.laneDescription,
            createNewLaneIfNoLaneId: true
          })
          await this.ensureNextVersion(oldVersion)
          break
        case 'add-card': {
          const index = this.cardTypes.findIndex((type) => type.title === args.cardTypeName)
          if (index >= 0) {
            const cardType = this.cardTypes[index]
            const cardTypeId = cardType.id
            const eventId = args.eventId || this.$route.params.eventId
            const isTagType = cardType.domainModelRole === DomainModelRole.TAG

            const cardData = {
              cardTypeId
            }

            if (isTagType) {
              cardData.tags = Array.isArray(args.cardText) ? args.cardText : []
            } else {
              cardData.description = args.cardText
            }

            const res = await workflowApi.createCard(this.$route.params.id, eventId, cardData)
            await this.ensureNextVersion(oldVersion)
            if (res && res.cards[0].id && cardTypeId === diagramHelpers.methods.getQueryCardType(this.cardTypes)?.id) {
              EventBus.$emit('define-read-model-table', { type: 'ai-workflow', cardId: res.cards[0].id })
            }
          }
          break
        }
        case 'add-command-schema': {
          const eventId = args.eventId || this.$route.params.eventId
          const event = this.workflow.domainEvents.find((e) => e.id === eventId)
          const name = event ? (cardHelpers.methods.getCommandName(event) || SchemaType.COMMAND) : SchemaType.COMMAND
          const fields = parseCommandFields({ fields: args.example_data, required: args.required })

          const newSchema = {
            name,
            type: args.type || SchemaType.COMMAND,
            fields
          }
          await workflowApi.addSchemaAndLinkCard({
            schema: newSchema,
            cardId: args.cardId,
            eventId,
            workflow: this.workflow,
            svgGraph: this.bpm.svgGraph
          })
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
        case 'add-domain-event-schema': {
          const eventId = args.eventId || this.$route.params.eventId
          const event = this.workflow.domainEvents.find((domainEvent) => domainEvent.id === eventId)
          const domainEventCard = event ? cardHelpers.methods.getDomainEventCard(event) : null
          const name = sanitizeCardDescription(domainEventCard?.description) || event?.description || ''
          const fields = parseDomainEventFields(args.example_data)

          const newSchema = {
            name,
            type: SchemaType.DOMAIN_EVENT,
            fields
          }
          await workflowApi.addSchemaAndLinkCard({
            schema: newSchema,
            cardId: args.cardId,
            eventId,
            workflow: this.workflow,
            svgGraph: this.bpm.svgGraph
          })
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
        case 'add-merged-read-model': {
          const newSchema = {
            id: args.schemaId,
            name: args.schemaName,
            type: SchemaType.QUERY,
            cardinality: args.cardinality,
            description: args.description,
            fields: args.fields
          }
          await workflowApi.addSchemaAndLinkCard({
            schema: newSchema,
            cardId: args.cardId,
            eventId: args.eventId,
            workflow: this.workflow,
            svgGraph: this.bpm.svgGraph
          })
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
        case 'link-read-model-card': {
          const updatedSvgGraph = safeStructuredClone(this.bpm.svgGraph)
          if (!updatedSvgGraph) {
            // Diagram state isn't available — fail with a clear message (the step is
            // caught and offered for retry) instead of a cryptic null-deref crash.
            throw new Error('Cannot link read model: diagram state unavailable')
          }
          const eventData = updatedSvgGraph[args.eventId]
          const cardIndex = eventData?.cards?.findIndex((card) => card.id === args.cardId)
          if (cardIndex >= 0) {
            eventData.cards[cardIndex].schemaId = args.schemaId
          }

          await workflowApi.updateWorkflow({
            svgGraph: updatedSvgGraph,
            workflow: this.workflow,
            updateNote: {
              action: 'linked read model card',
              target: ''
            }
          }, true)
          await this.ensureNextVersion(oldVersion)
          oldVersion = this.workflow.version
          break
        }
      }
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          const { errors, errorList } = Checks.checkDiagramConsistency(this.bpm.svgGraph, this.bpm.svgLanes, this.bpm.svgGroups, this.bpm.events)
          if (errors === 0) {
            resolve()
          } else {
            this.testResults = errorList
            reject(new Error('Failed with ' + errors + ' failed tests. ' + errorList.toString()))
          }
        }, delay)
      })
    },
    async ensureNextVersion (oldVersion) {
      while (oldVersion === this.workflow.version) {
        await this.wait100ms()
      }
    },
    async wait100ms () {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve()
        }, 100)
      })
    },
    async validatePrompt () {
      if (this.forceWithoutValidation) {
        return true
      }

      const attachments = this.workflow.aiAttachments || []
      if (!this.workflow.aiDescription && attachments.length === 0) {
        return true
      }

      let res
      try {
        res = await validateWorkflowDescription({
          workflow: this.workflow,
          attachments
        })
      } catch (err) {
        console.warn('Prompt validation skipped:', err)
        return true
      }

      if (res?.answer === 'no' && res?.feedback) {
        this.$loadingState.stop()
        this.validationMessage = res.feedback
        this.suggestedAiDescription = res.suggestion || ''
        this.showValidationDialog = true
        return false
      }

      return true
    },
    async forceInvalidatedAction () {
      try {
        this.forceWithoutValidation = true
        this.showValidationDialog = false
        await this.forcedAction()
      } catch (e) {
        console.log(e)
      } finally {
        this.forceWithoutValidation = false
      }
    },
    editPromptFromValidation () {
      this.showValidationDialog = false
      this.$nextTick(() => {
        this.$refs.aiDescription?.focusTextarea?.()
      })
    },
    cancelFromValidation () {
      this.showValidationDialog = false
      this.dialog = false
    },
    setSuggestion () {
      this.$nextTick(async () => {
        await this.saveAiDescription(this.suggestedAiDescription)
        this.showValidationDialog = false
        this.openSettingsOrGenerator()
      })
    },
    prepareForcedAction (fn, ...args) {
      return async function () {
        return await fn(...args)
      }
    }
  },
  data () {
    return {
      DomainModelRole,
      validForm: true,
      dialog: false,
      testResults: [],
      chatLimitRoleLength: 'Keep the role name to max 16 characters.',
      chatLimitDescriptionLength: 'Keep the description to one sentence and max 40 characters.',
      showValidationDialog: false,
      validationMessage: '',
      suggestedAiDescription: '',
      forceWithoutValidation: false,
      forcedAction: () => {},
      saveAiDescriptionDebouncer: debounce((e) => {
        this.saveAiDescription(e)
      }, 1000),
      isGenerating: false,
      chat: null,
      generateCommand: true,
      generateReadModel: true,
      generateEntity: true,
      abortController: null,
      readModelsForLinking: [],
      domainEventsForLinking: [],
      generatedEntitiesResponse: [],
      cardSectionExpanded: true
    }
  },
  computed: {
    ...mapState({
      workflow: (state) => state.workflow.workflow,
      cardTypes: (state) => state.cardTypes.cardTypes,
      failedGenerations: (state) => state.workflow.failedGenerations,
      organisations: (state) => state.organisations.organisations,
      projectMemberships: (state) => state.projectMemberships.projectMemberships
    }),
    examples () {
      return workflowDescriptionExamples
    },
    ...mapGetters({
      loadingState: 'loading/getLoadingState'
    }),
    // Resolve the org for the open workflow. Prefer the org-native activeProject
    // (gated getProject for the route projectId); fall back to the legacy membership
    // list. The legacy list is null for org users until a create/update populates it,
    // which previously left currentOrg null and failed effectiveAiVersions OPEN to the
    // full model catalog (bypassing the org's AI-model allowlist).
    currentOrg () {
      const projectId = this.$route.params.projectId
      if (!projectId || !this.organisations) {
        return null
      }
      const activeProject = this.$store.state.projectMemberships?.activeProject
      let organisationId = activeProject?.id === projectId ? activeProject?.organisationId : null
      if (!organisationId && this.projectMemberships) {
        organisationId = this.projectMemberships.find((m) => m.project?.id === projectId)?.project?.organisationId
      }
      if (!organisationId) {
        return null
      }
      return this.organisations.find((o) => o.id === organisationId) || null
    },
    // The models actually available for this workflow's org.
    availableVersions () {
      return effectiveAiVersions(AI_MODELS.map((m) => m.version), this.currentOrg)
    },
    // True when the org has no AI models available — block generation.
    noModelsAvailable () {
      return this.availableVersions.length === 0
    },
    // The selected model — the saved/default one, but ONLY if it's actually
    // available; otherwise null (nothing selected), which disables Generate and
    // shows the "Select a model" placeholder.
    selectedAiModel () {
      const candidate = this.workflow?.aiVersion ?? this.currentOrg?.defaultAiVersion ?? null

      return this.availableVersions.includes(candidate) ? candidate : null
    },
    selectedCardTypeCount () {
      return (this.cardTypes || []).filter((cardType) => cardType.generateWithAI).length
    }
  },
  watch: {
    isGenerating (newVal) {
      if (!newVal && this.chat) {
        this.chat.stopStreaming()
      }
    }
  },
  mounted () {
    EventBus.$on('generate-diagram', async (workflowId) => {
      if (!this.loadingState && (this.workflow.id === this.$route.params.id)) {
        this.generateDiagramFromText(false)
      }
    })
    EventBus.$on('add-card-with-ai', async (eventId, cardTypeId) => {
      this.addCardToDiagram(eventId, cardTypeId)
    })
  },
  beforeUnmount () {
    EventBus.$off('generate-diagram')
    EventBus.$off('add-card-with-ai')
  }
}
</script>

<style>
.generate-button {
  z-index: 1;
  padding-left: 10px;
  padding-right: 10px;
  text-transform: none !important;
}
</style>

<style scoped>
.btn-stop-generating {
  position: fixed;
  bottom: 64px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000000;
}

.card-section-header {
  cursor: pointer;
  user-select: none;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.card-section-header:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.card-section-chevron {
  transition: transform 0.2s ease;
}

.card-section-chevron--collapsed {
  transform: rotate(180deg);
}
</style>
